import { FilterBuilder, type SearchPayload } from "@atlas/sdk";
import { IHubCatalogStatus } from "@enums";
import type { IHubCatalogType } from "@models";
import type { HubManagerTabId } from "@shared";

export type { HubManagerTabId };

export interface IHubManagerCatalogTypeFilterItem {
  id: string;
  label: string;
}

export const HUB_CATALOG_TYPE_FILTER_ALL = "all" as const;

export function isHubCatalogTypeFilterAll(selectedTypeIds: string[]): boolean {
  return (
    selectedTypeIds.length === 0 ||
    selectedTypeIds.includes(HUB_CATALOG_TYPE_FILTER_ALL)
  );
}

export function resolveHubCatalogTypeFilterSelection(
  previousTypeIds: string[],
  nextTypeIds: string[],
): string[] {
  const hadAll = isHubCatalogTypeFilterAll(previousTypeIds);
  const hasAll = nextTypeIds.includes(HUB_CATALOG_TYPE_FILTER_ALL);

  if (hasAll && !hadAll) {
    return [HUB_CATALOG_TYPE_FILTER_ALL];
  }

  if (hasAll && hadAll && nextTypeIds.length > 1) {
    return nextTypeIds.filter(
      (typeId) => typeId !== HUB_CATALOG_TYPE_FILTER_ALL,
    );
  }

  const specificTypeIds = nextTypeIds.filter(
    (typeId) => typeId !== HUB_CATALOG_TYPE_FILTER_ALL,
  );

  if (specificTypeIds.length === 0) {
    return [HUB_CATALOG_TYPE_FILTER_ALL];
  }

  return specificTypeIds;
}

export function isSolutionHubCatalogType(type: IHubCatalogType): boolean {
  const n = type.name?.trim().toLowerCase() ?? "";
  const l = type.label?.trim().toLowerCase() ?? "";
  return n === "solution" || l === "solution";
}

export function orderCatalogTypesSolutionFirst(
  types: IHubCatalogType[],
): IHubCatalogType[] {
  const solutionTypes: IHubCatalogType[] = [];
  const otherTypes: IHubCatalogType[] = [];
  for (const type of types) {
    if (isSolutionHubCatalogType(type)) {
      solutionTypes.push(type);
    } else {
      otherTypes.push(type);
    }
  }
  return [...solutionTypes, ...otherTypes];
}

export function mapCatalogTypesToFilterItems(
  types: IHubCatalogType[],
): IHubManagerCatalogTypeFilterItem[] {
  return types.map((catalogType) => ({
    id: String(catalogType.typeId),
    label: catalogType.label || catalogType.name,
  }));
}

export function shouldShowHubCatalogTypeFilterAll(
  visibleTypeCount: number,
): boolean {
  return visibleTypeCount > 1;
}

export const HUB_MANAGER_CATALOGS_PREVIEW_LIMIT = 3;
export const HUB_MANAGER_CATALOGS_VIEW_ALL_LIMIT = 500;
export const HUB_EXPLORER_CATALOGS_PREVIEW_LIMIT = 3;
/** Page size for Explorer when a single catalog type filter is selected. */
export const HUB_EXPLORER_CATALOGS_FOCUSED_PAGE_SIZE = 8;
export const HUB_EXPLORER_CATALOGS_PAGE_SIZE =
  HUB_EXPLORER_CATALOGS_PREVIEW_LIMIT;

export function buildHubManagerCatalogSectionSearchPayload(
  activeTab: HubManagerTabId,
  catalogType: IHubCatalogType,
  searchQuery: string,
  userId: string,
  limit: number,
  startIndex = 0,
): SearchPayload {
  const trimmedSearch = searchQuery.trim();
  const typeId = catalogType.typeId ?? -1;

  if (activeTab === "explorer") {
    return {
      filter: new FilterBuilder()
        .in("status", [IHubCatalogStatus.Approved, IHubCatalogStatus.Submitted])
        .equals("typeId", typeId)
        .contains("label", trimmedSearch)
        .build(),
      sort: ["featured DESC"],
      mode: "DATA",
      startIndex,
      limit,
    };
  }

  return {
    filter: new FilterBuilder()
      .equals("createdBy", userId)
      .contains("label", trimmedSearch)
      .equals("typeId", typeId)
      .build(),
    sort: ["createdByDatetime DESC"],
    mode: "DATA",
    startIndex,
    limit,
  };
}

export function hubManagerCatalogSectionHasItems(section: {
  isSuccess: boolean;
  isLoading: boolean;
  totalCount: number;
}): boolean {
  if (!section.isSuccess || section.isLoading) {
    return false;
  }
  const count = Number(section.totalCount);
  return Number.isFinite(count) && count > 0;
}

export function filterCatalogTypesWithKnownItemCounts(
  types: IHubCatalogType[],
  countByTypeId: ReadonlyMap<string, number>,
): IHubCatalogType[] {
  return types.filter((catalogType) => {
    const count = countByTypeId.get(String(catalogType.typeId));
    if (count === undefined) {
      return true;
    }
    return count > 0;
  });
}

export function normalizeHubCatalogTypeFilterSelection(
  selectedTypeIds: string[],
  visibleTypeIds: string[],
  showAllOption: boolean,
): string[] {
  if (visibleTypeIds.length === 0) {
    return [HUB_CATALOG_TYPE_FILTER_ALL];
  }

  if (!showAllOption) {
    return [visibleTypeIds[0]];
  }

  const visibleTypeIdSet = new Set(visibleTypeIds);

  if (isHubCatalogTypeFilterAll(selectedTypeIds)) {
    return [HUB_CATALOG_TYPE_FILTER_ALL];
  }

  const specificSelectedTypeIds = selectedTypeIds.filter(
    (typeId) =>
      typeId !== HUB_CATALOG_TYPE_FILTER_ALL && visibleTypeIdSet.has(typeId),
  );

  if (specificSelectedTypeIds.length === 0) {
    return [HUB_CATALOG_TYPE_FILTER_ALL];
  }

  return specificSelectedTypeIds;
}

export function areHubCatalogTypeFilterSelectionsEqual(
  left: string[],
  right: string[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}

export function filterHubManagerCatalogTypesBySelection(
  catalogTypesWithItems: IHubCatalogType[],
  selectedTypeIds: string[],
): IHubCatalogType[] {
  if (isHubCatalogTypeFilterAll(selectedTypeIds)) {
    return catalogTypesWithItems;
  }
  return catalogTypesWithItems.filter((catalogType) =>
    selectedTypeIds.includes(String(catalogType.typeId)),
  );
}

export function areHubManagerCatalogTypeListsEqual(
  left: IHubCatalogType[],
  right: IHubCatalogType[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every(
    (catalogType, index) => catalogType.typeId === right[index]?.typeId,
  );
}
